import React, {useCallback, useEffect, useRef, useState} from 'react';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import {
  isImageFile,
  isVideoFile,
} from '../../../../data/models/LibraryFile/VideoSourceShort';
import {loadingLottie} from '../../../../assets/lottie';
import Lottie from 'lottie-react';
import {videoSourcesText} from '../../videoSourcesText';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {canUseLegacyVideoEditor} from '../../../../modules/MicroreelStudio/studioMedia';
import KebabButton from '../../../../uiToolkit/KebabButton';

const tooltip = <Tooltip>{videoSourcesText.fileIsUsed}</Tooltip>;
const cancelJobTooltip = (
  <Tooltip>{videoSourcesText.cancelJobDisabledTooltip}</Tooltip>
);

interface Props {
  file: MediaFile;
  onPreview: (file: MediaFile) => void;
  onEditVideo: (file: MediaFile) => void;
  onDownloadTranscript: (id: string) => Promise<any>;
  onDownloadVideo: (file: MediaFile) => Promise<any>;
  onDelete: (id: string) => void;
  isProcessing?: boolean;
  onRefresh?: () => void;
  onCancelJob?: (id: string) => void;
}

const LibraryFileOptions = React.memo(
  ({
    file,
    onPreview,
    onEditVideo,
    onDownloadTranscript,
    onDownloadVideo,
    onDelete,
    isProcessing,
    onRefresh,
    onCancelJob,
  }: Props) => {
    const [showOptions, setShowOptions] = useState(false);
    const [fileIsDownloading, setFileIsDownloading] = useState(false);

    const openerRef = useRef<any>(null);

    const onPreviewOption = () => {
      onPreview(file);
      setShowOptions(false);
    };

    const onDownloadTranscriptOption = () => {
      setFileIsDownloading(true);
      onDownloadTranscript(file.id).then(() => {
        setFileIsDownloading(false);
      });
    };

    const onDownloadVideoOption = () => {
      setFileIsDownloading(true);
      onDownloadVideo(file).then(() => {
        setFileIsDownloading(false);
      });
    };

    useEffect(() => {
      window.addEventListener('click', handleBodyClick);
      return () => {
        window.removeEventListener('click', handleBodyClick);
      };
    }, [showOptions]);

    const handleBodyClick = useCallback(
      (ev: any) => {
        if (showOptions && openerRef?.current !== ev.target) {
          setShowOptions(false);
        }
      },
      [showOptions],
    );

    const canShowPreview = () =>
      isImageFile(file.fileType) || isVideoFile(file.fileType);

    const canShowEditVideo = () =>
      file.fileUrl &&
      (file.fileStatus === 0 || file.fileStatus === 2) &&
      file.origin === 1 &&
      isVideoFile(file.fileType) &&
      canUseLegacyVideoEditor(file);

    return (
      <div className="LibraryFileOptions">
        <KebabButton
          isOpen={showOptions}
          disabled={fileIsDownloading}
          onClick={() => setShowOptions(!showOptions)}
          ref={openerRef}>
          {fileIsDownloading && (
            <Lottie
              animationData={loadingLottie}
              loop={true}
              style={{
                width: '16px',
                height: '16px',
              }}
            />
          )}
        </KebabButton>
        {showOptions && (
          <div className="Options">
            {isProcessing ? (
              <>
                <div
                  className={`Option`}
                  onClick={() => {
                    onRefresh?.();
                    setShowOptions(false);
                  }}>
                  {videoSourcesText.updateJob}
                </div>
                {file.canCancelGeneration ? (
                  <div
                    className={`Option DeleteOption`}
                    onClick={() => {
                      onCancelJob?.(file.id);
                      setShowOptions(false);
                    }}>
                    {videoSourcesText.cancelJob}
                  </div>
                ) : (
                  <OverlayTrigger placement="bottom" overlay={cancelJobTooltip}>
                    <div>
                      <div className={`Option DeleteOptionDisabled`}>
                        {videoSourcesText.cancelJob}
                      </div>
                    </div>
                  </OverlayTrigger>
                )}
              </>
            ) : (
              <>
                {canShowPreview() && (
                  <div className={`Option`} onClick={onPreviewOption}>
                    {videoSourcesText.preview}
                  </div>
                )}
                {canShowEditVideo() && (
                  <div className={`Option`} onClick={() => onEditVideo(file)}>
                    {videoSourcesText.editVideo}
                  </div>
                )}
                {file.fileUrl && (
                  <div className={`Option`} onClick={onDownloadVideoOption}>
                    {videoSourcesText.downloadFile}
                  </div>
                )}
                {file.hasTranscript && (
                  <div
                    className={`Option`}
                    onClick={onDownloadTranscriptOption}>
                    {videoSourcesText.downloadTranscript}
                  </div>
                )}
                {file.isUsed ? (
                  <OverlayTrigger placement="bottom" overlay={tooltip}>
                    <div>
                      <div className={`Option DeleteOptionDisabled`}>
                        {videoSourcesText.delete}
                      </div>
                    </div>
                  </OverlayTrigger>
                ) : (
                  <div
                    className={`Option DeleteOption`}
                    onClick={() => onDelete(file.id)}>
                    {videoSourcesText.delete}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default LibraryFileOptions;
